/**
 * Client-side file conversion via ConvertAPI (raw REST — the browser SDK hardcodes StoreFile=true and a
 * separate stored upload, both of which retain the file, and it doesn't take a JWT).
 *
 * reeve stores ONLY .docx in Azure, so any other upload format is converted to .docx in the browser BEFORE
 * it is PUT to storage; and .docx is converted to PDF in the browser on download. Each conversion runs
 * directly browser -> ConvertAPI, authorised by a SHORT-LIVED JWT minted by GET /api/convert/token — the
 * long-lived secret stays on the backend, exactly like the short-lived Azure SAS the upload PUT already
 * uses. `StoreFile=false` returns the bytes inline (base64) and keeps ConvertAPI from retaining the file;
 * the token response pins the UK region. The plaintext document never transits reeve's own servers.
 */
import { apiGet } from './apiClient';

const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

interface ConvertToken {
  jwt: string;
  baseUrl: string;
  expiresAt: string;
}

function getConvertToken(): Promise<ConvertToken> {
  return apiGet<ConvertToken>('/api/convert/token');
}

/** True if the file is already a Word .docx — reeve's storage format, so it skips conversion. */
export function isDocx(file: { name: string; type: string }): boolean {
  return /\.docx$/i.test(file.name) || file.type.includes('wordprocessingml');
}

/**
 * ConvertAPI source-format code for an uploaded file, or null if reeve doesn't take it. Only
 * text-bearing document formats are listed — reeve deliberately does NOT accept photos/scans (jpg/png/
 * tiff), because pulling text out of an image means OCR, which is lossy and can silently drop or mangle
 * things that matter on a compliance document (a signature, a date). We only ingest text we can read.
 */
function sourceFormat(fileName: string): string | null {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  const map: Record<string, string> = {
    pdf: 'pdf',
    doc: 'doc',
    odt: 'odt',
    html: 'html',
    htm: 'html',
    md: 'md',
    markdown: 'md',
  };
  return map[ext] ?? null;
}

/** Base64 (ConvertAPI FileData) -> a fresh ArrayBuffer (concrete, so it's a valid BlobPart). */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
  return buffer;
}

/**
 * One raw ConvertAPI conversion, StoreFile=false so the result comes back inline (no CDN hop, no retention).
 * Mints a fresh short-lived JWT per attempt and retries an auth/rate blip (a just-minted token can very
 * occasionally lose a signature-verification race, and 429s happen under burst) — a new token clears both.
 */
async function convertOnce(
  from: string,
  to: string,
  input: Blob,
  fileName: string,
  params: Record<string, string> = {},
): Promise<ArrayBuffer> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const token = await getConvertToken();
    const form = new FormData();
    form.append('File', input, fileName);
    form.append('StoreFile', 'false');
    for (const [name, value] of Object.entries(params)) form.append(name, value);

    const base = token.baseUrl.endsWith('/') ? token.baseUrl : `${token.baseUrl}/`;
    const response = await fetch(`${base}convert/${from}/to/${to}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token.jwt}` },
      body: form,
    });

    if (response.ok) {
      const json = (await response.json()) as { Files?: { FileData?: string }[] };
      const data = json.Files?.[0]?.FileData;
      if (data) return base64ToArrayBuffer(data);
      lastError = new Error('The conversion returned no file.');
      break; // a 200 with no file won't fix itself on retry
    }

    if (response.status === 401 || response.status === 429) {
      // Transient auth/rate blip — a fresh token on the next loop usually clears it.
      lastError = new Error(`Conversion temporarily unavailable (${response.status}).`);
      await new Promise((r) => setTimeout(r, 350 * (attempt + 1)));
      continue;
    }

    const body = await response.text().catch(() => '');
    throw new Error(`Conversion failed (${response.status})${body ? `: ${body.slice(0, 200)}` : ''}`);
  }
  throw lastError ?? new Error('Conversion failed.');
}

/**
 * Normalise an uploaded file to a .docx Blob ready to PUT to storage. Files already .docx pass through
 * unchanged (this also covers the editor's Accept-save path, which produces a .docx). Throws a
 * user-facing message on an unsupported format or a bad/empty conversion.
 */
export async function ensureDocx(
  file: File,
): Promise<{ blob: Blob; fileName: string; contentType: string }> {
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'document';
  if (isDocx(file)) return { blob: file, fileName: file.name, contentType: DOCX_TYPE };

  const from = sourceFormat(file.name);
  if (!from) {
    const ext = file.name.split('.').pop()?.toUpperCase() ?? 'this file';
    throw new Error(`reeve can't read ${ext} files. Try a PDF or Word document.`);
  }

  let docx: ArrayBuffer;
  if (from === 'md') {
    // No direct md->docx endpoint exists; chain md->html->docx (both are supported).
    const html = await convertOnce('md', 'html', file, file.name);
    docx = await convertOnce('html', 'docx', new Blob([html], { type: 'text/html' }), `${baseName}.html`);
  } else {
    // PDF gets a light-touch flowing layout (see the note in DocumentDetail on why the on-screen view can
    // still look off); every other supported input already carries a real text layer, so no params needed.
    const params: Record<string, string> = from === 'pdf' ? { Layout: 'flowing', OcrMode: 'auto' } : {};
    docx = await convertOnce(from, 'docx', file, file.name, params);
  }

  // Fail fast on a bad/empty conversion — a real .docx is a ZIP that starts with "PK".
  const head = new Uint8Array(docx);
  if (head.length < 4 || head[0] !== 0x50 || head[1] !== 0x4b) {
    throw new Error('The converted document was empty or invalid. Try a different file.');
  }
  return { blob: new Blob([docx], { type: DOCX_TYPE }), fileName: `${baseName}.docx`, contentType: DOCX_TYPE };
}

/** Convert a stored .docx (its bytes) to a PDF Blob for download. */
export async function docxToPdf(docx: ArrayBuffer, fileName = 'document.docx'): Promise<Blob> {
  const pdf = await convertOnce('docx', 'pdf', new Blob([docx], { type: DOCX_TYPE }), fileName);
  return new Blob([pdf], { type: 'application/pdf' });
}
